import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSaleMandates } from '../../hooks/useSaleMandates';
import { Modal } from '../ui/Modal';
import { SaleMandateForm } from './SaleMandateForm';
import { ExposeUpload } from './ExposeUpload';
import { VerkaufsinfoUpload } from './VerkaufsinfoUpload';
import { SaleMandate } from '../../types';
import { apiClient } from '../../api/client';

const COUNTRY_NAMES: Record<string, string> = {
  DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz', ES: 'Spanien',
  FR: 'Frankreich', IT: 'Italien', NL: 'Niederlande', GB: 'Vereinigtes Königreich',
  PL: 'Polen', PT: 'Portugal', LU: 'Luxemburg', US: 'USA',
};

const STATUS_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  active:     { label: 'Aktiv',         bg: 'var(--accent-light)',  color: 'var(--accent)' },
  versendet:  { label: 'Versendet',     bg: '#e3f2fd',              color: '#1565c0' },
  sold:       { label: 'Verkauft',      bg: '#e8f5e9',              color: '#2e7d32' },
  withdrawn:  { label: 'Zurückgezogen', bg: 'var(--surface2)',      color: 'var(--text3)' },
};

function formatPrice(eur?: number | null): string {
  if (!eur) return '—';
  if (eur >= 1_000_000) return `${(eur / 1_000_000).toFixed(2).replace(/\.?0+$/, '')} Mio. €`;
  if (eur >= 1_000) return `${(eur / 1_000).toFixed(0)} Tsd. €`;
  return `${eur} €`;
}

type SortDir = 'asc' | 'desc';

export function SaleMandateList() {
  const navigate = useNavigate();
  // URL-State (analog CompanyList): page, q, archived, status, asset_class_id, limit
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1'));
  const setPage = (next: number | ((p: number) => number)) => {
    const resolved = typeof next === 'function' ? next(page) : next;
    const sp = new URLSearchParams(searchParams);
    if (resolved <= 1) sp.delete('page'); else sp.set('page', String(resolved));
    setSearchParams(sp, { replace: true });
  };
  const search = searchParams.get('q') ?? '';
  const setSearch = (value: string) => {
    const sp = new URLSearchParams(searchParams);
    if (value) sp.set('q', value); else sp.delete('q');
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  };
  const archived = (searchParams.get('archived') ?? 'active') as 'active' | 'archived' | 'all';
  const setArchived = (v: 'active' | 'archived' | 'all') => {
    const sp = new URLSearchParams(searchParams);
    if (v === 'active') sp.delete('archived'); else sp.set('archived', v);
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  };
  const limitFromUrl = (() => {
    const n = parseInt(searchParams.get('limit') ?? '25');
    return [25, 50, 100].includes(n) ? n : 25;
  })();
  const setLimit = (n: number) => {
    const sp = new URLSearchParams(searchParams);
    if (n === 25) sp.delete('limit'); else sp.set('limit', String(n));
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  };
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') ?? 'created_at');
  const [sortDir, setSortDir] = useState<SortDir>((searchParams.get('sort_dir') as SortDir) ?? 'desc');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showVerkaufsinfoModal, setShowVerkaufsinfoModal] = useState(false);
  const [showExposeModal, setShowExposeModal] = useState(false);
  const limit = limitFromUrl;

  const { data, isLoading, error, refetch } = useSaleMandates({
    page, limit,
    search: search || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
    archived,
  });

  async function handleArchive(mandate: SaleMandate, ev: React.MouseEvent) {
    ev.stopPropagation();
    const reason = window.prompt(`Mandat "${mandate.title}" archivieren — optionaler Grund:`, '');
    if (reason === null) return;
    await apiClient.post(`/sale-mandates/${mandate.id}/archive`, { reason: reason || null });
    refetch();
  }
  async function handleUnarchive(mandate: SaleMandate, ev: React.MouseEvent) {
    ev.stopPropagation();
    if (!window.confirm(`Mandat "${mandate.title}" aus dem Archiv zurückholen?`)) return;
    await apiClient.post(`/sale-mandates/${mandate.id}/unarchive`, {});
    refetch();
  }
  async function handleDelete(mandate: SaleMandate, ev: React.MouseEvent) {
    ev.stopPropagation();
    const hasComm = (mandate.send_count ?? 0) > 0 || (mandate.thread_count ?? 0) > 0;
    if (hasComm) {
      window.alert(`„${mandate.title}" hat bereits Kommunikation (${mandate.send_count ?? 0} versendet, ${mandate.thread_count ?? 0} Threads). Bitte stattdessen archivieren.`);
      return;
    }
    if (!window.confirm(`„${mandate.title}" wirklich löschen?\n\nDieses Mandat hat noch keine Kommunikation — der Löschvorgang ist unbedenklich. Die Aktion kann nicht rückgängig gemacht werden.`)) return;
    try {
      await apiClient.delete(`/sale-mandates/${mandate.id}`);
      refetch();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? (err as Error).message;
      window.alert(`Fehler: ${msg}`);
    }
  }

  const handleSort = (col: string, defaultDir: SortDir = 'asc') => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(defaultDir);
    }
    setPage(1);
  };

  const sortIcon = (col: string) => {
    if (sortBy !== col) return <span style={{ opacity: 0.3, fontSize: '10px', marginLeft: '4px' }}>↕</span>;
    return <span style={{ fontSize: '10px', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const thStyle = (col: string, align: 'left' | 'right' | 'center' = 'left'): React.CSSProperties => ({
    padding: '10px 16px', textAlign: align, fontWeight: 500,
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    color: sortBy === col ? 'var(--text)' : undefined,
  });

  const mandates: SaleMandate[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const inputStyle: React.CSSProperties = {
    padding: '7px 12px', border: '1px solid var(--border2)',
    borderRadius: 'var(--radius)', fontSize: '13px',
    background: 'var(--surface)', color: 'var(--text)',
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search" placeholder="Mandate suchen..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ ...inputStyle, flex: 1, minWidth: '180px' }}
        />
        <button
          onClick={() => setShowCreateModal(true)}
          style={{ padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
        >
          + Neues Mandat
        </button>
        <button
          onClick={() => setShowVerkaufsinfoModal(true)}
          style={{ padding: '7px 14px', background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
        >
          Aus Verkaufsinfo
        </button>
        <button
          onClick={() => setShowExposeModal(true)}
          style={{ padding: '7px 14px', background: 'var(--surface)', color: 'var(--text2)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer' }}
        >
          Exposé importieren
        </button>
        <select
          value={archived}
          onChange={e => setArchived(e.target.value as 'active' | 'archived' | 'all')}
          style={{ ...inputStyle, padding: '7px 8px', minWidth: '120px' }}
          title="Archiv-Filter"
        >
          <option value="active">Aktive</option>
          <option value="archived">Archiviert</option>
          <option value="all">Alle</option>
        </select>
        <select
          value={limit}
          onChange={e => setLimit(parseInt(e.target.value))}
          style={{ ...inputStyle, padding: '7px 8px', minWidth: '70px' }}
          title="Pro Seite"
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--accent)' }}>Fehler beim Laden.</div>
        ) : mandates.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>
            {search ? 'Keine Treffer.' : 'Noch keine Verkaufsmandate.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface2)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)' }}>
                <th style={thStyle('title')} onClick={() => handleSort('title')}>
                  Bezeichnung{sortIcon('title')}
                </th>
                <th style={thStyle('asset_class')} onClick={() => handleSort('asset_class')}>
                  Asset{sortIcon('asset_class')}
                </th>
                <th style={thStyle('city')} onClick={() => handleSort('city')}>
                  Standort{sortIcon('city')}
                </th>
                <th style={thStyle('asking_price_eur', 'right')} onClick={() => handleSort('asking_price_eur', 'desc')}>
                  Kaufpreis{sortIcon('asking_price_eur')}
                </th>
                <th style={thStyle('status')} onClick={() => handleSort('status')}>
                  Status{sortIcon('status')}
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 500 }}>MACH</th>
                <th style={thStyle('created_at', 'right')} onClick={() => handleSort('created_at', 'desc')}>
                  Erstellt{sortIcon('created_at')}
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 500, width: '80px' }}>Archiv</th>
              </tr>
            </thead>
            <tbody>
              {mandates.map((m: SaleMandate, idx: number) => (
                <tr key={m.id}
                  onClick={() => navigate(`/verkaufsmandate/${m.id}`)}
                  style={{ borderTop: idx > 0 ? '1px solid var(--border)' : undefined, cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <td style={{ padding: '11px 16px', fontWeight: 500, fontSize: '13px' }}>{m.title}</td>
                  <td style={{ padding: '11px 16px', fontSize: '12px', color: 'var(--text2)' }}>
                    {m.asset_classes?.[0] ?? '—'}
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: '12px', color: 'var(--text2)' }}>
                    {[m.city, m.country_code ? COUNTRY_NAMES[m.country_code] ?? m.country_code : null].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td style={{ padding: '11px 16px', fontSize: '12px', color: 'var(--text)', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 500 }}>
                    {formatPrice(m.asking_price_eur)}
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    {(() => {
                      const s = STATUS_LABELS[m.status];
                      return (
                        <span style={{
                          padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 500,
                          background: s?.bg ?? 'var(--surface2)',
                          color: s?.color ?? 'var(--text3)',
                        }}>
                          {s?.label ?? m.status}
                        </span>
                      );
                    })()}
                    {(m.send_count ?? 0) > 0 && (
                      <div style={{ fontSize: '10px', color: 'var(--text3)', marginTop: '2px' }}>
                        ✉ {m.send_count}× versendet
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => navigate(`/verkaufsmandate/${m.id}?tab=investoren`)}
                      style={{
                        padding: '4px 12px', fontSize: '12px', fontWeight: 600,
                        background: (m.match_count ?? 0) > 0 ? '#388e3c' : 'var(--accent)',
                        color: '#fff',
                        border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {(m.match_count ?? 0) > 0 ? `MACH · ${m.match_count}` : 'MACH'}
                    </button>
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono, monospace', whiteSpace: 'nowrap' }}>
                    {new Date(m.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'center', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    {(() => {
                      const hasComm = (m.send_count ?? 0) > 0 || (m.thread_count ?? 0) > 0;
                      if (m.archived_at) {
                        return (
                          <button
                            onClick={(e) => handleUnarchive(m, e)}
                            style={{ padding: '3px 10px', fontSize: '11px', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text2)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
                            title="Aus Archiv zurückholen"
                          >
                            wiederherstellen
                          </button>
                        );
                      }
                      return (
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button
                            onClick={(e) => handleArchive(m, e)}
                            style={{ padding: '3px 10px', fontSize: '11px', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text3)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
                            title="Mandat archivieren (Daten bleiben erhalten)"
                          >
                            archivieren
                          </button>
                          {!hasComm && (
                            <button
                              onClick={(e) => handleDelete(m, e)}
                              style={{ padding: '3px 10px', fontSize: '11px', border: '1px solid #d32f2f', background: 'var(--surface)', color: '#d32f2f', borderRadius: 'var(--radius)', cursor: 'pointer' }}
                              title="Mandat löschen (möglich da keine Kommunikation)"
                            >
                              löschen
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', fontSize: '13px', color: 'var(--text2)' }}>
          <span>{total} Mandate gesamt</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '6px 12px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px' }}>
              ← Zurück
            </button>
            <span style={{ padding: '7px 12px', color: 'var(--text3)' }}>{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ padding: '6px 12px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px' }}>
              Weiter →
            </button>
          </div>
        </div>
      )}

      {/* Modal: Neues Mandat */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Neues Verkaufsmandat" size="md">
        <SaleMandateForm
          onSuccess={() => { setShowCreateModal(false); refetch(); }}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      {/* Modal: Mandat aus Verkaufsinformation */}
      <Modal isOpen={showVerkaufsinfoModal} onClose={() => setShowVerkaufsinfoModal(false)} title="Mandat aus Verkaufsinformation anlegen" size="md">
        <VerkaufsinfoUpload
          onSuccess={() => { setShowVerkaufsinfoModal(false); refetch(); }}
          onCancel={() => setShowVerkaufsinfoModal(false)}
        />
      </Modal>

      {/* Modal: Exposé importieren */}
      <Modal isOpen={showExposeModal} onClose={() => setShowExposeModal(false)} title="Exposé importieren" size="md">
        <ExposeUpload
          onSuccess={() => { setShowExposeModal(false); refetch(); }}
          onCancel={() => setShowExposeModal(false)}
        />
      </Modal>
    </div>
  );
}
